"use client"

import type React from "react"

import { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { toast } from "react-toastify"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { PasswordInput } from "../../components/ui/password-input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { Label } from "../../components/ui/label"
import Navbar from "../../components/Navbar"
import Footer from "../../components/Footer"
import { authService } from "../../services/api"
import { Loader2 } from "lucide-react"
import { useLanguage } from "../../contexts/LanguageContext"

export default function RegisterPage() {
  const { t } = useLanguage()
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    confirmPassword: "",
  })
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const validateForm = () => {
    // 验证用户名
    if (formData.username.length < 3) {
      toast.error(t('validation.usernameTooShort'))
      return false
    }

    if (formData.username.length > 20) {
      toast.error(t('validation.usernameTooLong'))
      return false
    }

    // 验证用户名格式（只允许字母、数字、下划线）
    const usernameRegex = /^[a-zA-Z0-9_]+$/
    if (!usernameRegex.test(formData.username)) {
      toast.error(t('validation.usernameInvalid'))
      return false
    }

    // 验证密码
    if (formData.password.length < 6) {
      toast.error(t('validation.passwordTooShort'))
      return false
    }

    if (formData.password.length > 50) {
      toast.error(t('validation.passwordTooLong'))
      return false
    }

    // 验证密码确认
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('validation.passwordMismatch'))
      return false
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      const response = await authService.register({
        username: formData.username,
        password: formData.password,
      })

      toast.success(t('register.success'))
      navigate("/login")
    } catch (error: any) {
      console.error('注册失败:', error)
      
      // 处理不同类型的错误
      let errorMessage = t('register.failed')
      
      if (error.details) {
        // 处理API返回的详细错误信息
        const details = error.details
        
        // 检查嵌套的错误结构：外层success: true，内层data.success: false
        if (details.data && typeof details.data === 'object') {
          const innerData = details.data
          if (innerData.success === false && innerData.message) {
            errorMessage = innerData.message
          } else if (innerData.message) {
            errorMessage = innerData.message
          }
        } else if (details.data && details.data.message) {
          errorMessage = details.data.message
        } else if (details.message) {
          errorMessage = details.message
        } else if (details.errorLevel) {
          // 根据错误级别显示不同信息
          switch (details.errorLevel) {
            case 'ERROR':
              errorMessage = '注册失败，请检查输入信息'
              break
            case 'WARNING':
              errorMessage = '注册警告，请确认信息'
              break
            default:
              errorMessage = error.message || t('register.failed')
          }
        }
      } else if (error.message) {
        // 直接使用错误消息
        errorMessage = error.message
      } else if (error.code) {
        // 根据错误代码显示不同信息
        switch (error.code) {
          case 400:
            errorMessage = '请求参数错误，请检查输入'
            break
          case 409:
            errorMessage = '用户名已存在，请选择其他用户名'
            break
          case 422:
            errorMessage = '输入数据验证失败，请检查格式'
            break
          case 500:
            errorMessage = '服务器内部错误，请稍后重试'
            break
          default:
            errorMessage = t('register.failed')
        }
      }
      
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Navbar />

      <div className="pt-20 pb-16 px-4">
        <div className="max-w-md mx-auto">
          <Card className="shadow-lg">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold text-gray-900">{t('register.title')}</CardTitle>
              <CardDescription>{t('register.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="username">{t('register.username')}</Label>
                  <Input
                    id="username"
                    name="username"
                    type="text"
                    required
                    value={formData.username}
                    onChange={handleChange}
                    className="mt-1"
                    placeholder={t('register.usernamePlaceholder')}
                    minLength={3}
                    maxLength={20}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('register.usernameHint')}
                  </p>
                </div>

                <PasswordInput
                  id="password"
                  name="password"
                  label={t('register.password')}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('register.passwordPlaceholder')}
                  minLength={6}
                  maxLength={50}
                />

                <PasswordInput
                  id="confirmPassword"
                  name="confirmPassword"
                  label={t('register.confirmPassword')}
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder={t('register.confirmPasswordPlaceholder')}
                  minLength={6}
                  maxLength={50}
                />

                <Button type="submit" disabled={loading} className="w-full bg-blue-800 hover:bg-blue-900">
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('register.creating')}
                    </>
                  ) : (
                    t('register.createAccount')
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  {t('register.haveAccount')}{" "}
                  <Link to="/login" className="text-blue-800 hover:text-blue-900 font-medium">
                    {t('register.signIn')}
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  )
}
